import graphql from 'babel-plugin-relay/macro'
import React, { useCallback, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { createFragmentContainer } from 'react-relay'
import styled from 'styled-components'

import { authenticateWithParticipantShortId } from '../../api'

const Container = styled.div`
  padding: 16px;
  background-color: #ecffff;
  flex-direction: column;
  border-top: 4px solid #48b1bf;
`

const Form = styled.form`
  flex: 1;
  display: flex;
  align-items: 'center';
  padding-top: 16px;
`

const Input = styled.input`
  flex: 1;
  font-family: 'Open Sans', sans-serif;
  font-size: 1em;
  padding: 8px;
  outline: 1px solid #ccc;

  :focus {
    outline: 1px solid #48b1bf;
  }
`

const Button = styled.button`
  margin-left: 8px;
  padding: 0 16px;
  border-radius: 4px;
  border-width: 0;
  background-color: #48b1bf;
  font-family: 'Open Sans';
  font-weight: bold;
  color: #fff;
  text-transform: uppercase;

  :disabled {
    background-color: #999;
  }
`

const Intro = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 0.9em;
`

const Hint = styled.div`
  margin-top: 8px;
  font-family: 'Open Sans', sans-serif;
  font-size: 0.7em;
  color: #666;
`

const Error = styled.div`
  color: red;
`

const SignUpInput = ({ t, participant, onUserCreated }) => {
  const [name, setName] = useState('')
  const [submitEnabled, setSubmitEnabled] = useState(false)
  const [error, setError] = useState('')

  const handleSignUp = useCallback(
    async e => {
      e.preventDefault()
      setSubmitEnabled(false)

      const {
        shortId,
        contact: { phone, email }
      } = participant

      try {
        const {
          authenticateWithParticipantShortId: { jwt }
        } = await authenticateWithParticipantShortId(
          shortId,
          name,
          phone,
          email
        )
        onUserCreated && onUserCreated(jwt)
      } catch (err) {
        setError(err.message)
      }
    },
    [participant, name, onUserCreated]
  )

  const handleChangeText = useCallback(({ target: { value } }) => {
    setName(value)
    setSubmitEnabled(!!value)
  }, [])

  const { users, participantsList } = useMemo(() => {
    const {
      conversation: { userParticipants }
    } = participant

    const list =
      userParticipants?.edges
        ?.map(
          ({ node: { user, family } }) =>
            (user && user.firstName) || (family && family.name)
        )
        .filter(v => v) || []

    const usersString = list.join(', ').replace(/, ([^,]*)$/, ' and $1')

    return { users: usersString, participantsList: list }
  }, [participant])

  return (
    <Container>
      {participantsList.length > 0 ? (
        <Intro
          dangerouslySetInnerHTML={{
            __html: `<b>${users}</b> ${
              participantsList.length === 1 ? 'is' : 'are'
            } already using this app to manage their family communication and align daily life. Join now by entering your name.`
          }}
        />
      ) : (
        <Intro>Join now by entering your name.</Intro>
      )}

      <Form onSubmit={handleSignUp}>
        <Input
          type='text'
          value={name}
          required
          autoFocus
          placeholder={t('conversation_signup_input_placeholder')}
          onChange={handleChangeText}
        />
        <Button type='submit' disabled={!submitEnabled}>{`Join`}</Button>
      </Form>
      <Hint
        dangerouslySetInnerHTML={{
          __html: `By joining, you agree to the <a href="https://familiohq.com/terms" target="_blank">Terms of Use</a> and <a href="https://familiohq.com/privacy" target="_blank">Privacy Policy</a>.`
        }}
      />
      {error ? <Error>{error}</Error> : null}
    </Container>
  )
}

const SignUpInputContainer = createFragmentContainer(SignUpInput, {
  participant: graphql`
    fragment SignUpInput_participant on Participant {
      shortId
      contact: contactByContactId {
        phone
        email
      }
      conversation: conversationByConversationId {
        userParticipants: participantsByConversationId {
          edges {
            node {
              user: userByUserId {
                firstName
              }
              family: familyByFamilyId {
                name
              }
            }
          }
        }
      }
    }
  `
})

const TranslatedSignUpInput = withTranslation()(SignUpInputContainer)
export { TranslatedSignUpInput as SignUpInput }
